<template>
  <section v-if="data?.count >= 5 && filteredItems.length" class="seo-links">
    <h2>{{ title }}</h2>
    <div>
      <input
        id="checkbox-engagement-facets"
        ref="showCheckbox"
        class="hidden-checkbox"
        type="checkbox"
        aria-hidden="true"
        tabindex="-1"
      />
      <nuxt-link
        v-for="item of filteredItems.slice(0, 9)"
        :key="item.slug"
        :to="item.url"
      >
        {{ item.name }}
      </nuxt-link>

      <template v-if="filteredItems.length > 9">
        <div class="additional-content">
          <template v-for="item in filteredItems.slice(9)" :key="item.url">
            <nuxt-link :to="item.url">
              {{ item.name }}
            </nuxt-link>
          </template>
        </div>

        <label for="checkbox-engagement-facets" :aria-label="$t('View more')">
          <translate class="section-close-txt">View fewer</translate>
          <translate class="section-open-txt">View more</translate>
        </label>
      </template>
    </div>
  </section>
</template>

<script setup lang="ts">
import { logger } from '../../../utility/logger'
import { UnifiedCampsiteSearchService } from '../infrastructure/UnifiedCampsiteSearchService'

const { $t } = useNuxtApp()
const searchStore = useSearchStoreSetup(usePinia())
const route = useRoute()
const lang = (route.params.lang as string) || 'en-gb'

interface Item {
  name: string
  slug: string
  url: string
}

interface Props {
  items: Item[]
  title: string
}

// This ensures both props are required and properly typed
const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  title: undefined, // undefined means required
})

const unifiedSearchService = new UnifiedCampsiteSearchService()

onUnmounted(() => {
  if (unifiedSearchService && unifiedSearchService.cancel) {
    unifiedSearchService.cancel()
  }
})

const currentSearchFilters = computed(() => {
  return searchStore.searchFilters
})

const { data } = useAsyncData(
  `engagement-facets-${JSON.stringify(searchStore.searchFilters)}`,
  async () => {
    const filters = {
      ...searchStore.searchFilters,
      facets: [],
      limit: 0,
      base: null,
      search_url: null,
      facet_counts_only: 'keyword',
    }

    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- UnifiedSearchService returns untyped data
      const results = await unifiedSearchService.search(lang, filters)
      logger('EngagementFacets', results)

      /* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- TODO: Type search results properly */
      const count =
        results.bookableCount !== undefined
          ? results.bookableCount
          : results.count
      /* eslint-enable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unnecessary-type-assertion -- TODO: Type search results properly
      const facetKeywords = (results as any).facetKeywords
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Type search results properly
      return { count, facetKeywords }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- error is caught but not used
    } catch (_error) {
      return { count: 0, facetKeywords: {} }
    }
  },
  {
    watch: [currentSearchFilters],
    server: true,
    lazy: process.client,
    dedupe: 'cancel',
  },
)

const filteredItems = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- facetKeywords from search results
  return props.items.filter((item) => data.value?.facetKeywords[item.slug] > 0)
  // return props.items
})
</script>

<style scoped>
@import url('~/assets/css/seo-links.css');
</style>
